import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Clock,
  CheckCircle,
  AlertCircle,
  Wallet,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { formatCurrency, cn } from '../../lib/utils';
import {
  CryptoQuotation,
  useGetQuotation,
  useCryptoPurchase,
  useCryptoWallets,
} from '../../hooks/useCrypto';

interface CryptoBuyModalProps {
  isOpen: boolean;
  onClose: () => void;
  virtualAccountId: string;
  quotations: CryptoQuotation[];
}

type Step = 'select' | 'amount' | 'confirm' | 'success';

const CRYPTO_OPTIONS = [
  { value: 'BTC', label: 'Bitcoin (BTC)' },
  { value: 'ETH', label: 'Ethereum (ETH)' },
  { value: 'USDT', label: 'Tether (USDT)' },
  { value: 'USDC', label: 'USD Coin (USDC)' },
];

const NETWORK_OPTIONS = [
  { value: 'ETHEREUM', label: 'Ethereum' },
  { value: 'BSC', label: 'BNB Smart Chain' },
  { value: 'TRON', label: 'TRON' },
  { value: 'BITCOIN', label: 'Bitcoin' },
];

export function CryptoBuyModal({ isOpen, onClose, virtualAccountId, quotations }: CryptoBuyModalProps) {
  const [step, setStep] = useState<Step>('select');
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [selectedNetwork, setSelectedNetwork] = useState('ETHEREUM');
  const [amountBrl, setAmountBrl] = useState('');
  const [selectedWallet, setSelectedWallet] = useState<string>('');
  const [quotationExpiry, setQuotationExpiry] = useState<number>(0);
  const [purchaseResult, setPurchaseResult] = useState<any>(null);

  const { data: wallets = [] } = useCryptoWallets(virtualAccountId);
  const getQuotation = useGetQuotation(virtualAccountId);
  const executePurchase = useCryptoPurchase(virtualAccountId);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('select');
      setSelectedCrypto('BTC');
      setSelectedNetwork('ETHEREUM');
      setAmountBrl('');
      setSelectedWallet('');
      setQuotationExpiry(0);
      setPurchaseResult(null);
      getQuotation.reset();
      executePurchase.reset();
    }
  }, [isOpen]);

  // Countdown timer for quotation
  useEffect(() => {
    if (getQuotation.data?.expiresAt && step === 'confirm') {
      const interval = setInterval(() => {
        const remaining = Math.max(0, new Date(getQuotation.data.expiresAt).getTime() - Date.now());
        setQuotationExpiry(Math.floor(remaining / 1000));
        if (remaining <= 0) {
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [getQuotation.data?.expiresAt, step]);

  const handleGetQuotation = async () => {
    if (!amountBrl || parseFloat(amountBrl) <= 0) return;
    
    try {
      await getQuotation.mutateAsync({
        network: selectedNetwork,
        cryptoSymbol: selectedCrypto,
        amountBrl: parseFloat(amountBrl),
      });
      setStep('confirm');
    } catch (error) {
      console.error('Error getting quotation:', error);
    }
  };

  const handleConfirmPurchase = async () => {
    if (!getQuotation.data || !selectedWallet) return;
    
    const wallet = wallets.find(w => w.id === selectedWallet);
    if (!wallet) return;

    try {
      const result = await executePurchase.mutateAsync({
        quotationId: getQuotation.data.id,
        destinationAddress: wallet.address,
      });
      setPurchaseResult(result);
      setStep('success');
    } catch (error) {
      console.error('Error executing purchase:', error);
    }
  };

  const currentQuote = quotations.find(q => (q.symbol || q.currency) === selectedCrypto);
  const estimatedCrypto = currentQuote && amountBrl 
    ? parseFloat(amountBrl) / currentQuote.priceBrl 
    : 0;

  const compatibleWallets = wallets.filter(w => 
    w.network === selectedNetwork || 
    (selectedNetwork === 'ETHEREUM' && w.network === 'ETH') ||
    (selectedNetwork === 'BSC' && w.network === 'BNB') ||
    (selectedNetwork === 'BITCOIN' && w.network === 'BTC')
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Comprar Criptomoeda" size="lg">
      <AnimatePresence mode="wait">
        {/* Step 1: Select Crypto and Network */}
        {step === 'select' && (
          <motion.div
            key="select"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Criptomoeda"
                options={CRYPTO_OPTIONS}
                value={selectedCrypto}
                onChange={(e) => setSelectedCrypto(e.target.value)}
              />
              <Select
                label="Rede"
                options={NETWORK_OPTIONS}
                value={selectedNetwork}
                onChange={(e) => setSelectedNetwork(e.target.value)}
              />
            </div>

            {currentQuote && (
              <Card className="bg-surface-800/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-surface-400 text-sm">Cotação Atual</p>
                    <p className="text-2xl font-bold text-surface-100">
                      {formatCurrency(currentQuote.priceBrl)}
                    </p>
                  </div>
                  <div className={cn(
                    'flex items-center gap-1 text-sm',
                    currentQuote.change24h >= 0 ? 'text-green-400' : 'text-red-400'
                  )}>
                    {currentQuote.change24h >= 0 ? '+' : ''}{currentQuote.change24h.toFixed(2)}%
                  </div>
                </div>
              </Card>
            )}

            <Input
              label="Valor em R$"
              type="number"
              placeholder="0,00"
              value={amountBrl}
              onChange={(e) => setAmountBrl(e.target.value)}
              min="10"
              step="0.01"
            />

            {estimatedCrypto > 0 && (
              <div className="p-4 rounded-lg bg-primary-500/10 border border-primary-500/30">
                <p className="text-surface-400 text-sm">Você receberá aproximadamente:</p>
                <p className="text-xl font-bold text-primary-400">
                  {estimatedCrypto.toFixed(8)} {selectedCrypto}
                </p>
                <p className="text-xs text-surface-500 mt-1">
                  * Valor estimado. A cotação final será travada no próximo passo.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={onClose}>Cancelar</Button>
              <Button 
                onClick={() => setStep('amount')} 
                disabled={!amountBrl || parseFloat(amountBrl) < 10}
              >
                Continuar
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Select Wallet */}
        {step === 'amount' && (
          <motion.div
            key="amount"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="p-4 rounded-lg bg-surface-800/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-surface-400">Comprando</span>
                <span className="text-surface-100 font-medium">{selectedCrypto}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-surface-400">Rede</span>
                <span className="text-surface-100 font-medium">{selectedNetwork}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-surface-400">Valor</span>
                <span className="text-surface-100 font-medium">{formatCurrency(parseFloat(amountBrl))}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Carteira de Destino
              </label>
              {compatibleWallets.length > 0 ? (
                <div className="space-y-2">
                  {compatibleWallets.map((wallet) => (
                    <button
                      key={wallet.id}
                      onClick={() => setSelectedWallet(wallet.id)}
                      className={cn(
                        'w-full p-4 rounded-lg border text-left transition-all',
                        selectedWallet === wallet.id
                          ? 'border-primary-500 bg-primary-500/10'
                          : 'border-surface-700 bg-surface-800/50 hover:border-surface-600'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Wallet className="w-5 h-5 text-primary-400" />
                          <div>
                            <p className="text-surface-100 font-medium">
                              {wallet.label || wallet.network}
                            </p>
                            <code className="text-xs text-surface-500">
                              {wallet.address.slice(0, 12)}...{wallet.address.slice(-8)}
                            </code>
                          </div>
                        </div>
                        {selectedWallet === wallet.id && (
                          <CheckCircle className="w-5 h-5 text-primary-400" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-center">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
                  <p className="text-surface-300">Nenhuma carteira compatível encontrada</p>
                  <p className="text-sm text-surface-500 mt-1">
                    Você precisa criar uma carteira na rede {selectedNetwork} primeiro.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-between gap-3">
              <Button variant="ghost" onClick={() => setStep('select')}>Voltar</Button>
              <Button 
                onClick={handleGetQuotation} 
                disabled={!selectedWallet || getQuotation.isPending}
              >
                {getQuotation.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Obtendo cotação...
                  </>
                ) : (
                  <>
                    Obter Cotação
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Confirm with locked quotation */}
        {step === 'confirm' && getQuotation.data && (
          <motion.div
            key="confirm"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Timer */}
            <div className={cn(
              'flex items-center justify-center gap-2 p-3 rounded-lg',
              quotationExpiry > 30 ? 'bg-green-500/10 text-green-400' :
              quotationExpiry > 10 ? 'bg-yellow-500/10 text-yellow-400' :
              'bg-red-500/10 text-red-400'
            )}>
              <Clock className="w-5 h-5" />
              <span className="font-medium">
                Cotação expira em {quotationExpiry}s
              </span>
            </div>

            {/* Quotation Details */}
            <Card className="bg-surface-800/50">
              <h3 className="text-lg font-semibold text-surface-100 mb-4">Resumo da Compra</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-surface-400">Valor</span>
                  <span className="text-surface-100 font-medium">
                    {formatCurrency(getQuotation.data.amountBrl / 100)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Você receberá</span>
                  <span className="text-primary-400 font-bold">
                    {getQuotation.data.amountCrypto.toFixed(8)} {selectedCrypto}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Taxa de rede</span>
                  <span className="text-surface-100">
                    {formatCurrency(getQuotation.data.fees.network / 100)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Taxa da plataforma</span>
                  <span className="text-surface-100">
                    {formatCurrency(getQuotation.data.fees.platform / 100)}
                  </span>
                </div>
                <div className="border-t border-surface-700 pt-3 flex justify-between">
                  <span className="text-surface-300 font-medium">Total</span>
                  <span className="text-surface-100 font-bold">
                    {formatCurrency((getQuotation.data.amountBrl + getQuotation.data.fees.total) / 100)}
                  </span>
                </div>
              </div>
            </Card>

            {/* Wallet destination */}
            <div className="p-3 rounded-lg bg-surface-800/50">
              <p className="text-surface-400 text-sm mb-1">Carteira de destino</p>
              <code className="text-surface-100 text-sm">
                {wallets.find(w => w.id === selectedWallet)?.address}
              </code>
            </div>

            {quotationExpiry <= 0 ? (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-center">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-400" />
                <p className="text-surface-300">Cotação expirada</p>
                <Button 
                  variant="secondary" 
                  size="sm" 
                  className="mt-2"
                  onClick={handleGetQuotation}
                >
                  Obter nova cotação
                </Button>
              </div>
            ) : (
              <div className="flex justify-between gap-3">
                <Button variant="ghost" onClick={() => setStep('amount')}>Voltar</Button>
                <Button 
                  onClick={handleConfirmPurchase} 
                  disabled={executePurchase.isPending}
                >
                  {executePurchase.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    'Confirmar Compra'
                  )}
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {/* Step 4: Success */}
        {step === 'success' && purchaseResult && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center"
            >
              <CheckCircle className="w-10 h-10 text-green-400" />
            </motion.div>

            <h3 className="text-2xl font-bold text-surface-100 mb-2">
              Compra Realizada!
            </h3>
            <p className="text-surface-400 mb-6">
              Sua compra de {selectedCrypto} foi processada com sucesso.
            </p>

            <Card className="bg-surface-800/50 text-left mb-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-surface-400">Quantidade</span>
                  <span className="text-primary-400 font-bold">
                    {purchaseResult.amountCrypto} {selectedCrypto}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Valor pago</span>
                  <span className="text-surface-100">
                    {formatCurrency(purchaseResult.amountBrl / 100)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Status</span>
                  <Badge variant="success">{purchaseResult.status}</Badge>
                </div>
                {purchaseResult.txHash && (
                  <div className="pt-3 border-t border-surface-700">
                    <p className="text-surface-400 text-sm mb-1">Hash da transação</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-surface-300 bg-surface-800 px-2 py-1 rounded flex-1 truncate">
                        {purchaseResult.txHash}
                      </code>
                      <a
                        href={`https://etherscan.io/tx/${purchaseResult.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 hover:bg-surface-700 rounded"
                      >
                        <ExternalLink className="w-4 h-4 text-primary-400" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Button onClick={onClose} className="w-full">
              Fechar
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
}

export default CryptoBuyModal;
